import { useMutation, useQuery } from '@tanstack/react-query'
import type { ClinicContact, ClinicRef, Practitioner } from '@/features/appointments/api'
import type { PdfLink } from '@/features/documents/api'
import { useApi } from '@/lib/api-context'

/**
 * O plano de tratamento encerrado como a paciente o vê (docs/api/portal.md,
 * `GET /portal/treatment-plans/:id` e `/pdf`; RN-POR-23): os produtos com lote, validade e regiões,
 * as etapas com quantas fotos cada uma tem e a cópia dela em PDF, a mesma do e-mail.
 */

export type PlanStage = 'marking' | 'before' | 'immediately_after' | 'after'

export interface TreatmentPlanProduct {
  name: string | null
  manufacturer: string | null
  lotNumber: string | null
  /** `aaaa-mm-dd`. */
  expiryDate: string | null
  regions: string[]
}

export interface TreatmentPlanSummary {
  id: string
  clinic: ClinicRef
  /** Só quando o atendimento também aparece no portal. */
  appointmentId: string | null
  closedAt: string
  /** `aaaa-mm-dd`. */
  procedureDate: string | null
  /** Os procedimentos do contrato ou "Plano de tratamento". */
  title: string
  procedures: { name: string }[]
  practitioner: Practitioner | null
  /** Nulo só no plano importado do sistema anterior. */
  protocolNumber: string | null
  imageConsent: 'yes' | 'no' | 'not_provided'
  products: TreatmentPlanProduct[]
  /** "2 produtos aplicados". */
  summary: string
  hasPdf: boolean
  isImported: boolean
}

export interface TreatmentPlanDetail extends TreatmentPlanSummary {
  clinic: ClinicContact
  stages: { stage: PlanStage; label: string; photoCount: number }[]
  /** O uso de imagem por extenso, com o que muda no PDF dela. */
  imageConsentText: string
}

export const treatmentPlanKeys = {
  detail: (id: string) => ['portal', 'treatment-plan', id] as const,
}

export function useTreatmentPlan(id: string) {
  const api = useApi()
  return useQuery({
    queryKey: treatmentPlanKeys.detail(id),
    queryFn: () => api.get<TreatmentPlanDetail>(`/portal/treatment-plans/${id}`),
  })
}

export function useTreatmentPlanPdf() {
  const api = useApi()
  return useMutation({
    mutationFn: (id: string) => api.get<PdfLink>(`/portal/treatment-plans/${id}/pdf`),
  })
}
